import { hypot, atan2 } from "yage/utils/deterministicMath";
import { bounds } from "./geometry.js";
import type { Point, Rect } from "./types.js";
export interface Chevron { x: number; y: number; angle: number }
export const CHEVRON_SPACING = 46;
/** Pizza-style wayfinding: a trail of chevrons marching from the captain toward the current
 *  objective. Empty when the objective is already at hand. Pure geometry, no engine state. */
export function chevronTrail(from: Point, to: Point, frame: number, march = 0.9): Chevron[] {
    const dx = to.x - from.x, dy = to.y - from.y, dist = hypot(dx, dy);
    if (!Number.isFinite(dist) || dist < 130)
        return [];
    const angle = atan2(dy, dx), ux = dx / dist, uy = dy / dist;
    const offset = frame * march % CHEVRON_SPACING;
    const out: Chevron[] = [];
    for (let d = 56 + offset; d <= dist - 48; d += CHEVRON_SPACING)
        out.push({ x: from.x + ux * d, y: from.y + uy * d, angle });
    return out.slice(0, 5);
}
/** Static orthogonal navigation grid. Same order and ties on every peer; no random or wall-clock state. */
export class AirportNav {
    readonly cols: number;
    readonly rows: number;
    readonly blocked: Uint8Array;
    constructor(readonly width: number, readonly height: number, readonly cell: number, readonly walls: Rect[], readonly radius = 18) {
        this.cols = Math.ceil(width / cell);
        this.rows = Math.ceil(height / cell);
        this.blocked = new Uint8Array(this.cols * this.rows);
        for (let y = 0; y < this.rows; y++)
            for (let x = 0; x < this.cols; x++) {
                const p = this.point(y * this.cols + x);
                if (!this.clear(p))
                    this.blocked[y * this.cols + x] = 1;
            }
    }
    point(i: number): Point { return { x: (i % this.cols + 0.5) * this.cell, y: (Math.floor(i / this.cols) + 0.5) * this.cell }; }
    index(p: Point): number { return Math.max(0, Math.min(this.rows - 1, Math.floor(p.y / this.cell))) * this.cols + Math.max(0, Math.min(this.cols - 1, Math.floor(p.x / this.cell))); }
    clear(p: Point): boolean { return p.x >= 26 + this.radius && p.x < this.width - 26 - this.radius && p.y >= 200 + this.radius && p.y < this.height - 24 - this.radius && !this.walls.some(r => { const b = bounds(r); return p.x >= b.left - this.radius && p.x <= b.right + this.radius && p.y >= b.top - this.radius && p.y <= b.bottom + this.radius; }); }
    /** A cell is walkable if it is clear and not on land east of the current airport's fence. */
    private open(i: number, maxX: number) { return !this.blocked[i] && this.point(i).x <= maxX - this.radius; }
    nearest(p: Point, maxX = Infinity): number {
        let start = this.index(p);
        if (this.open(start, maxX))
            return start;
        const sx = start % this.cols, sy = Math.floor(start / this.cols);
        for (let r = 1; r < Math.max(this.cols, this.rows); r++) {
            let best = -1, dist = Infinity;
            for (let y = Math.max(0, sy - r); y <= Math.min(this.rows - 1, sy + r); y++)
                for (let x = Math.max(0, sx - r); x <= Math.min(this.cols - 1, sx + r); x++) {
                    if (Math.abs(x - sx) !== r && Math.abs(y - sy) !== r)
                        continue;
                    const i = y * this.cols + x;
                    if (!this.open(i, maxX))
                        continue;
                    const q = this.point(i), dx = q.x - p.x, dy = q.y - p.y, d = dx * dx + dy * dy;
                    if (d < dist) {
                        best = i;
                        dist = d;
                    }
                }
            if (best >= 0)
                return best;
        }
        return -1;
    }
    visible(a: Point, b: Point): boolean {
        const steps = Math.ceil(hypot(b.x - a.x, b.y - a.y) / (this.cell / 3));
        for (let n = 0; n <= steps; n++) {
            const t = steps ? n / steps : 0;
            if (!this.clear({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t }))
                return false;
        }
        return true;
    }
    /** Grid path from one point to another. Nothing east of maxX (the locked airport land) is walkable. */
    path(from: Point, to: Point, maxX = Infinity): number[] {
        if (!Number.isFinite(from.x + from.y + to.x + to.y))
            return [];
        if (to.x > maxX - this.radius) to = { x: maxX - this.radius, y: to.y };
        const start = this.nearest(from, maxX), end = this.nearest(to, maxX);
        if (start < 0 || end < 0)
            return [];
        const target = this.clear(to) ? to : this.point(end);
        if (this.visible(from, target))
            return [target.x, target.y];
        const count = this.blocked.length, previous = new Int32Array(count).fill(-1), cost = new Int32Array(count).fill(2147483647), closed = new Uint8Array(count);
        const heap: {
            id: number;
            f: number;
        }[] = [];
        const h = (i: number) => Math.abs(i % this.cols - end % this.cols) + Math.abs(Math.floor(i / this.cols) - Math.floor(end / this.cols));
        const less = (a: {
            id: number;
            f: number;
        }, b: {
            id: number;
            f: number;
        }) => a.f < b.f || (a.f === b.f && a.id < b.id);
        const push = (id: number, f: number) => { const v = { id, f }; heap.push(v); let i = heap.length - 1; while (i > 0) {
            const p = (i - 1) >> 1;
            if (!less(v, heap[p]))
                break;
            heap[i] = heap[p];
            i = p;
        } heap[i] = v; };
        const pop = () => { const r = heap[0], v = heap.pop()!; if (heap.length) {
            let i = 0;
            while (i * 2 + 1 < heap.length) {
                let j = i * 2 + 1;
                if (j + 1 < heap.length && less(heap[j + 1], heap[j]))
                    j++;
                if (!less(heap[j], v))
                    break;
                heap[i] = heap[j];
                i = j;
            }
            heap[i] = v;
        } return r; };
        cost[start] = 0;
        push(start, h(start));
        while (heap.length) {
            const { id } = pop();
            if (closed[id])
                continue;
            if (id === end)
                break;
            closed[id] = 1;
            const x = id % this.cols, y = Math.floor(id / this.cols);
            for (const next of [x > 0 ? id - 1 : -1, y > 0 ? id - this.cols : -1, x + 1 < this.cols ? id + 1 : -1, y + 1 < this.rows ? id + this.cols : -1]) {
                if (next < 0 || closed[next] || !this.open(next, maxX) || cost[id] + 1 >= cost[next])
                    continue;
                cost[next] = cost[id] + 1;
                previous[next] = id;
                push(next, cost[next] + h(next));
            }
        }
        if (start !== end && previous[end] < 0)
            return [];
        const nodes: Point[] = [target];
        for (let n = end; n !== start && n >= 0; n = previous[n])
            nodes.push(this.point(n));
        nodes.push(this.point(start));
        nodes.reverse();
        const out: number[] = [];
        let anchor = this.clear(from) ? from : nodes[0], i = 0;
        while (i < nodes.length) {
            let furthest = i;
            for (let j = i; j < nodes.length; j++) {
                if (this.visible(anchor, nodes[j]))
                    furthest = j;
                else
                    break;
            }
            anchor = nodes[furthest];
            out.push(anchor.x, anchor.y);
            i = furthest + 1;
        }
        return out;
    }
}
